import { useEffect, useMemo, useState } from 'react';
import type { Participant, RoomView, UserProfile } from '@shared/protocol';
import { Avatar } from './Avatar';
import { GlobeIcon, SettingsIcon, ShareIcon } from './icons';

type Tab = 'mine' | 'shared';

interface Props {
  me?: UserProfile;
  rooms: RoomView[];
  currentRoomId?: string;
  participants: Participant[];
  onSwitchRoom: (id: string) => void;
  onCreateRoom: () => void;
  onManageRoom: (room: RoomView) => void;
  /** Your artifacts, Bitbucket, connections and sign-out, all behind one icon. */
  onOpenSettings: () => void;
  /** No Bitbucket key registered yet, so the entry point says so rather than hiding. */
  gitKeyMissing?: boolean;
  /**
   * A Figma or Atlassian connection stopped working. Not connecting at all
   * is a choice and gets no mark; a connection that silently died does.
   */
  connectionBroken?: boolean;
}

/** How a room says what it is, in the space of one line. */
function subtitleOf(room: RoomView): string | undefined {
  if (room.mine) {
    if (room.visibility === 'everyone') return 'Everyone';
    if (room.sharedWith.length === 1) return `Shared with 1 person`;
    if (room.sharedWith.length > 1) return `Shared with ${room.sharedWith.length} people`;
    return undefined; // private and unshared: the default, so it says nothing
  }
  if (!room.ownerEmail) return 'Everyone';
  return room.ownerName ?? room.ownerEmail;
}

export function Rail({
  me,
  rooms,
  currentRoomId,
  participants,
  onSwitchRoom,
  onCreateRoom,
  onManageRoom,
  onOpenSettings,
  gitKeyMissing,
  connectionBroken,
}: Props) {
  const [tab, setTab] = useState<Tab>('mine');

  const mine = useMemo(() => rooms.filter((r) => r.mine), [rooms]);
  const shared = useMemo(() => rooms.filter((r) => !r.mine), [rooms]);

  // Follow the room the person is actually in. Switching into a colleague's
  // room and watching the sidebar keep insisting on "Mine" -- with the open
  // room nowhere in the list -- reads as a bug.
  useEffect(() => {
    const current = rooms.find((r) => r.id === currentRoomId);
    if (current) setTab(current.mine ? 'mine' : 'shared');
  }, [currentRoomId, rooms]);

  const shown = tab === 'mine' ? mine : shared;

  const alert = gitKeyMissing || connectionBroken;
  const alertText = gitKeyMissing
    ? 'Bitbucket anahtarın tanımlı değil, push yapamazsın'
    : connectionBroken
      ? 'bir bağlantı yeniden bağlanmayı bekliyor'
      : '';

  return (
    <aside className="rail">
      <div className="rail__brand">
        <img className="rail__brand-mark" src="/kaizen-mark.png" alt="" />
        <span className="rail__brand-name">Cowork</span>
      </div>

      <div className="rail__section rail__section--grow">
        <div className="rail__label">
          <span>Rooms</span>
          <button className="rail__add" onClick={onCreateRoom} title="New room" aria-label="New room">
            +
          </button>
        </div>

        <div className="segmented" role="tablist" aria-label="Which rooms to show">
          <button
            role="tab"
            aria-selected={tab === 'mine'}
            className="segmented__option"
            onClick={() => setTab('mine')}
          >
            Mine
            <span className="segmented__count">{mine.length}</span>
          </button>
          <button
            role="tab"
            aria-selected={tab === 'shared'}
            className="segmented__option"
            onClick={() => setTab('shared')}
          >
            Shared
            <span className="segmented__count">{shared.length}</span>
          </button>
        </div>

        {shown.length === 0 && (
          <p className="rail__empty">
            {tab === 'mine'
              ? 'Rooms you create are private to you until you share them.'
              : 'Nothing shared with you yet.'}
          </p>
        )}

        {shown.map((room) => {
          const subtitle = subtitleOf(room);
          return (
            <div className="room-row" key={room.id}>
              <button
                className="room"
                aria-current={room.id === currentRoomId}
                onClick={() => onSwitchRoom(room.id)}
              >
                <span className={`dot dot--${room.status}`} aria-hidden />
                <span className="room__text">
                  <span className="room__name">{room.name}</span>
                  {subtitle && <span className="room__sub">{subtitle}</span>}
                </span>
                {room.visibility === 'everyone' && (
                  <GlobeIcon size={13} className="room__glyph" />
                )}
                {room.participantCount > 0 && (
                  <span className="room__count">{room.participantCount}</span>
                )}
              </button>

              {room.canManage && (
                <button
                  className="room__manage"
                  onClick={() => onManageRoom(room)}
                  title={`Share "${room.name}"`}
                  aria-label={`Share "${room.name}"`}
                >
                  <ShareIcon size={13} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="rail__section">
        <div className="rail__label">
          <span>In this room</span>
          <span>{participants.length}</span>
        </div>
        {participants.map((p) => (
          <div className="person" key={p.id}>
            <Avatar user={p} />
            <span className="person__name">
              {p.name}
              {p.connections > 1 && (
                <span className="room__count"> ·{p.connections}</span>
              )}
            </span>
            {p.typingSince && <span className="person__typing">typing</span>}
          </div>
        ))}
      </div>

      {me && (
        <div className="rail__footer">
          <Avatar user={me} />
          <span className="person__name" title={me.email}>
            {me.name}
          </span>
          <button
            className={`rail__icon${alert ? ' rail__icon--alert' : ''}`}
            onClick={onOpenSettings}
            title={alertText ? `Ayarlar — ${alertText}` : 'Ayarlar'}
            aria-label={alertText ? `Ayarlar — ${alertText}` : 'Ayarlar'}
          >
            <SettingsIcon size={15} />
            {/* The label is gone, so the unset state needs a mark of its own. */}
            {alert && <span className="rail__icon-dot" aria-hidden />}
          </button>
        </div>
      )}
    </aside>
  );
}
